import React from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Paper,
} from "@mui/material";
import {
  Phone,
  Instagram,
  Telegram,
  WhatsApp,
  RateReview,
  Groups,
  PersonSearch,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

// Map social platforms to icons and colors
const SocialIconMap = {
  instagram: { icon: Instagram, color: "#E1306C" },
  telegram: { icon: Telegram, color: "#2AABEE" },
  whatsapp: { icon: WhatsApp, color: "#25D366" },
};

export default function ShortDetails({ gymData, onOpenRating }) {
  const theme = useTheme();

  const getCoachesCount = () => gymData?.coachesCount || 0;
  const getCapacity = () => gymData?.capacity || 0;
  const getPhone = () => gymData?.phone || "شماره ثبت نشده";
  const getPricing = () =>
    gymData?.pricing?.length > 0 ? gymData.pricing : [{ price: 0 }];

  const getSocialMediaUrl = (platform) => {
    return (
      gymData?.socialMedia?.find((s) => s?.platform === platform)?.url || null
    );
  };

  return (
    <Paper
      component="aside"
      elevation={0}
      sx={{
        position: { lg: "sticky" },
        top: { lg: 24 },
        p: { xs: 2.5, sm: 3 },
        borderRadius: 4,
        bgcolor: "rgba(18, 18, 18, 0.6)",
        border: `1px solid rgba(255, 255, 255, 0.08)`,
        backdropFilter: "blur(20px)",
        boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
      }}
    >
      {/* Quick Stats */}
      <Stack gap={2.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(255,255,255,0.05)" }}
            >
              <PersonSearch fontSize="small" sx={{ color: "text.primary" }} />
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              مربیان حرفه‌ای
            </Typography>
          </Stack>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            color="secondary.main"
          >
            {getCoachesCount()}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(255,255,255,0.05)" }}
            >
              <Groups fontSize="small" sx={{ color: "text.primary" }} />
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              ظرفیت همزمان
            </Typography>
          </Stack>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            color="secondary.main"
          >
            {getCapacity()} نفر
          </Typography>
        </Stack>

        <Box
          sx={{
            bgcolor: "rgba(92, 107, 192, 0.08)",
            p: 1.5,
            borderRadius: 3,
            border: `1px solid ${theme.palette.primary.main}30`,
            mt: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={0.25}
          >
            شروع تعرفه‌ها از
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            color="primary.light"
          >
            {getPricing()[0].price.toLocaleString()}{" "}
            <Typography component="span" variant="caption">
              تومان
            </Typography>
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.05)" }} />

      {/* Social Media */}
      <Box sx={{ mb: 3 }}>
        <Typography
          component="h2"
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2, color: "text.primary" }}
        >
          شبکه‌های اجتماعی
        </Typography>
        <Stack direction="row" gap={1}>
          {["instagram", "telegram", "whatsapp"].map((platform) => {
            const url = getSocialMediaUrl(platform);
            if (!url) return null;
            const config = SocialIconMap[platform];
            const Icon = config.icon;
            return (
              <Tooltip key={platform} title={platform}>
                <IconButton
                  size="small"
                  onClick={() => window.open(url, "_blank")}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.05)",
                    transition: "all 0.3s",
                    "&:hover": {
                      bgcolor: config.color + "20",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Icon sx={{ color: config.color, fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            );
          })}
        </Stack>
      </Box>

      {/* Actions */}
      <Stack gap={1.5}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Phone sx={{ ml: ".5rem" }} />}
          onClick={() => (window.location.href = `tel:${getPhone()}`)}
          sx={{ py: 1.2, fontSize: "0.9rem", fontWeight: 700, borderRadius: 3 }}
        >
          تماس مستقیم
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<RateReview sx={{ ml: ".5rem" }} />}
          onClick={onOpenRating}
          sx={{
            py: 1,
            fontSize: "0.9rem",
            color: "text.primary",
            borderColor: "rgba(255,255,255,0.2)",
            borderRadius: 3,
            fontWeight: 700,
            "&:hover": {
              borderColor: theme.palette.secondary.main,
              color: theme.palette.secondary.main,
            },
          }}
        >
          ثبت امتیاز
        </Button>
      </Stack>

      {/* Map Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          mt: 3,
          bgcolor: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Typography
          component="h2"
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2 }}
        >
          موقعیت روی نقشه
        </Typography>
        {gymData?.location?.linkMap ? (
          <Box
            dangerouslySetInnerHTML={{ __html: gymData.location.linkMap }}
            sx={{
              width: "100%",
              "& iframe": {
                width: "100% !important",
                height: "180px !important",
                border: "none",
                borderRadius: "12px",
                filter:
                  theme.palette.mode === "dark"
                    ? "invert(90%) hue-rotate(180deg) brightness(95%) contrast(85%)"
                    : "none",
              },
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            نقشه‌ای ثبت نشده است.
          </Typography>
        )}
      </Paper>

      {/* Working Status */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          bgcolor: "rgba(0, 229, 255, 0.05)",
          borderRadius: 3,
          textAlign: "center",
          border: `1px dashed ${theme.palette.secondary.main}30`,
        }}
      >
        <Typography variant="caption" color="secondary.main" fontWeight={600}>
          {gymData?.workingHours?.length === 0
            ? "۲۴ ساعته - همه روزه پذیرای شما هستیم"
            : "بدون نیاز به رزرو آنلاین - پذیرای شما هستیم"}
        </Typography>
      </Box>
    </Paper>
  );
}
